import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, Download, Filter, Database, TrendingUp, AlertTriangle, Globe, Shield } from 'lucide-react';
import axios from 'axios';

const PartnerRiskTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [riskLevelFilter, setRiskLevelFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortField, setSortField] = useState('partnerRiskScore');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedMineral, setSelectedMineral] = useState('Lithium');
  const [selectedYear, setSelectedYear] = useState('2024');

  // Available minerals and years
  const minerals = ['All', 'Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite'];
  const years = ['All', '2020', '2021', '2022', '2023', '2024'];

  // Fetch partner risk data
  const fetchData = async (mineral = selectedMineral, year = selectedYear) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/critical-minerals/dashboard', {
        params: { 
          mineral: mineral === 'All' ? 'All' : mineral, 
          year: year === 'All' ? '2024' : year, 
          metric: 'Partner Risk' 
        }
      });
      
      if (response.data && response.data.countries) {
        // Transform the data to include mineral and year info
        const transformedData = response.data.countries.map((country, index) => ({
          id: index + 1,
          mineral: mineral,
          year: year,
          country: country.name,
          iso3: country.iso3,
          partnerRiskScore: country.partnerRisk || 0,
          wgiScore: country.wgiScore || 0,
          fsiScore: country.fsiScore || 0,
          dependencyRatio: country.dependencyRatio || 0,
          importPercentage: country.importPercentage || 0,
          tradeVolume: country.tradeVolume || 0,
          riskLevel: country.riskLevel || 'Medium',
          compositeRisk: country.compositeRisk || 0,
          supplyConcentration: country.supplyConcentration || 0,
          // Add trade data similar to Import Ratio table
          importValue: country.tradeData?.importValue || 0,
          exportValue: country.tradeData?.exportValue || 0,
          tradeBalance: country.tradeData?.tradeBalance || 0,
          exportPercentage: country.tradeData?.exportPercentage || 0
        }));
        
        setData(transformedData);
        setFilteredData(transformedData);
      } else {
        setData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error('Error fetching partner risk data:', error);
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter data based on search and filters
  useEffect(() => {
    let filtered = data;

    // Search filter
    if (searchText) {
      filtered = filtered.filter(item =>
        item.country?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.iso3?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.mineral?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Risk level filter based on partner risk score
    if (riskLevelFilter !== 'all') {
      filtered = filtered.filter(item => {
        const partnerRiskScore = item.partnerRiskScore || 0;
        switch (riskLevelFilter) {
          case 'high':
            return partnerRiskScore <= 3.0;
          case 'medium':
            return partnerRiskScore > 3.0 && partnerRiskScore <= 3.8;
          case 'low':
            return partnerRiskScore > 3.8;
          default:
            return true;
        }
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [data, searchText, riskLevelFilter]);


  // Risk level color mapping based on partner risk score
  const getRiskLevelColor = (partnerRiskScore) => {
    if (partnerRiskScore <= 3.0) return 'bg-red-100 text-red-800 border-red-200';
    if (partnerRiskScore <= 3.8) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-green-100 text-green-800 border-green-200';
  };

  // Get risk level text based on partner risk score
  const getRiskLevel = (partnerRiskScore) => {
    if (partnerRiskScore <= 3.0) return 'High';
    if (partnerRiskScore <= 3.8) return 'Medium';
    return 'Low';
  };

  // WGI score color mapping
  const getWgiScoreColor = (wgiScore) => {
    if (wgiScore >= 0.5) return 'text-green-600';
    if (wgiScore >= 0) return 'text-yellow-600';
    if (wgiScore >= -0.5) return 'text-orange-600';
    return 'text-red-600';
  };

  // FSI score color mapping (lower is better)
  const getFsiScoreColor = (fsiScore) => {
    if (fsiScore <= 30) return 'text-green-600';
    if (fsiScore <= 60) return 'text-yellow-600';
    if (fsiScore <= 90) return 'text-orange-600';
    return 'text-red-600';
  };

  // Sort function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sorted and paginated data
  const getSortedData = () => {
    const sorted = [...filteredData].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal?.toLowerCase() || '';
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    const startIndex = (currentPage - 1) * itemsPerPage;
    return sorted.slice(startIndex, startIndex + itemsPerPage);
  };

  // Export functionality
  const handleExport = () => {
    const csvContent = [
      // Headers
      ['Mineral', 'Year', 'Country', 'ISO3', 'Partner Risk Score', 'WGI Score', 'FSI Score', 'Risk Level', 'Composite Risk', 'Supply Concentration'].join(','),
      // Data rows
      ...filteredData.map(item => [
        item.mineral,
        item.year,
        `"${item.country?.replace(/"/g, '""') || ''}"`,
        item.iso3 || '',
        item.partnerRiskScore || 0,
        item.wgiScore || 0,
        item.fsiScore || 0,
        item.riskLevel || 'Medium',
        item.compositeRisk || 0,
        item.supplyConcentration || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `partner_risk_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const sortedData = getSortedData();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Calculate statistics
  const totalCountries = data.length;
  const avgPartnerRiskScore = data.length > 0 ? (data.reduce((sum, item) => sum + (item.partnerRiskScore || 0), 0) / data.length).toFixed(1) : 0;
  const highRiskCountries = data.filter(item => (item.partnerRiskScore || 0) <= 3.0).length;
  const mediumRiskCountries = data.filter(item => (item.partnerRiskScore || 0) > 3.0 && (item.partnerRiskScore || 0) <= 3.8).length;
  const lowRiskCountries = data.filter(item => (item.partnerRiskScore || 0) > 3.8).length;
  
  // Additional statistics
  const avgDependencyRatio = data.length > 0 ? (data.reduce((sum, item) => sum + (item.dependencyRatio || 0), 0) / data.length).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading partner risk data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <Shield className="h-8 w-8 mr-3 text-primary-600" />
                Partner Risk Data Table
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Complete view of partner risk assessment for critical minerals by country
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => fetchData()}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleExport}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Globe className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Countries
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {totalCountries}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Avg Partner Risk Score
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {avgPartnerRiskScore}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      High Risk Countries
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {highRiskCountries}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-orange-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Medium Risk Countries
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {mediumRiskCountries}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Low Risk Countries
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {lowRiskCountries}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Data Selection and Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Data Selection & Filters</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search countries, minerals..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mineral
                </label>
                <select
                  value={selectedMineral}
                  onChange={(e) => {
                    setSelectedMineral(e.target.value);
                    fetchData(e.target.value, selectedYear);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {minerals.map(mineral => (
                    <option key={mineral} value={mineral}>
                      {mineral}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(e.target.value);
                    fetchData(selectedMineral, e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {years.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Risk Level Filter
                </label>
                <select
                  value={riskLevelFilter}
                  onChange={(e) => setRiskLevelFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">All Risk Levels</option>
                  <option value="high">High (≤3.0)</option>
                  <option value="medium">Medium (3.1-3.8)</option>
                  <option value="low">Low (&gt;3.8)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    #
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('country')}
                  >
                    Country {sortField === 'country' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    ISO3
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('partnerRiskScore')}
                  >
                    Partner Risk Score {sortField === 'partnerRiskScore' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('wgiScore')}
                  >
                    WGI Score {sortField === 'wgiScore' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('fsiScore')}
                  >
                    FSI Score {sortField === 'fsiScore' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Risk Level
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedData.map((item) => (
                  <tr key={`${item.mineral}-${item.year}-${item.country}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {item.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {item.country}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {item.iso3}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRiskLevelColor(item.partnerRiskScore)}`}>
                          {item.partnerRiskScore?.toFixed(1) || 'N/A'}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          {getRiskLevel(item.partnerRiskScore)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`font-medium ${getWgiScoreColor(item.wgiScore)}`}>
                        {item.wgiScore?.toFixed(2) || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`font-medium ${getFsiScoreColor(item.fsiScore)}`}>
                        {item.fsiScore?.toFixed(1) || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRiskLevelColor(item.partnerRiskScore)}`}>
                        {item.riskLevel || getRiskLevel(item.partnerRiskScore)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{' '}
                  <span className="font-medium">
                    {Math.min((currentPage - 1) * itemsPerPage + 1, filteredData.length)}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, filteredData.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{filteredData.length}</span>{' '}
                  results
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerRiskTable;
